import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import { APP_TOKEN_KEY } from "./appAuthSlice";
import {
  legMissingScoreResult,
  mergeLegFixtureMeta,
  normalizeBettorTickets,
  resolveLegScore,
  ticketNeedsFixtureHydration,
  ticketsNeedFixtureHydration,
} from "../utils/bettorsDeskHelpers";
import { normalizeHouseTicketList } from "../utils/ticketsDeskHelpers";
import { normalizeViewerList } from "../utils/viewersDeskHelpers";
import { normalizeFollowerList } from "../utils/followersDeskHelpers";

const hydrateOperatorTicket = async (houseId, ticket, operatorUserId, client) => {
  let merged = normalizeBettorTickets([ticket])[0];
  if (ticketNeedsFixtureHydration(merged)) {
    try {
      const detailRes = await client.get(
        `/api/bet/${houseId}/tickets/${merged.ticketId}`,
        { params: { userId: operatorUserId } }
      );
      const detail = detailRes.data?.data || detailRes.data;
      if (detail?.legs?.length) {
        merged = normalizeBettorTickets([{ ...merged, ...detail }])[0];
      }
    } catch (_error) {
      /* keep list row if detail fails */
    }
  }
  const [withScores] = await hydrateTicketLegScores([merged], client);
  return normalizeBettorTickets([withScores])[0];
};

const hydrateTicketLegScores = async (tickets, client) => {
  const fixtureIds = new Set();
  for (const ticket of tickets) {
    for (const leg of ticket.legs || []) {
      if (!leg?.fixtureId || resolveLegScore(leg)) continue;
      if (
        legMissingScoreResult(leg, ticket.status) ||
        ["won", "lost", "paid"].includes(String(ticket.status || "").toLowerCase())
      ) {
        fixtureIds.add(String(leg.fixtureId));
      }
    }
  }
  if (!fixtureIds.size) return tickets;

  const metaByFixtureId = new Map();
  await Promise.all(
    [...fixtureIds].map(async (fixtureId) => {
      try {
        const res = await client.get(`/api/general-chat/fixtures/${fixtureId}/meta`);
        const meta = res.data?.data;
        if (meta) metaByFixtureId.set(fixtureId, meta);
      } catch (_error) {
        /* ignore per-fixture failures */
      }
    })
  );

  if (!metaByFixtureId.size) return tickets;

  return tickets.map((ticket) => ({
    ...ticket,
    legs: (ticket.legs || []).map((leg) => {
      const meta = metaByFixtureId.get(String(leg.fixtureId));
      return meta ? mergeLegFixtureMeta(leg, meta) : leg;
    }),
  }));
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(APP_TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const fetchOperatorHouses = createAsyncThunk(
  "operatorConsole/fetchHouses",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/get-bets", { userId });
      const list = Array.isArray(res.data) ? res.data : [];
      return list.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load your markets");
    }
  }
);

export const fetchOperatorHousesSummary = createAsyncThunk(
  "operatorConsole/fetchHousesSummary",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/get-bets", { userId, summary: true });
      const list = Array.isArray(res.data) ? res.data : [];
      return list.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load your markets"
      );
    }
  }
);

export const fetchOperatorHouseDetail = createAsyncThunk(
  "operatorConsole/fetchHouseDetail",
  async ({ userId, betId }, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/get-bets", { userId, betId });
      const list = Array.isArray(res.data) ? res.data : [];
      const house = list[0];
      if (!house?._id) {
        return rejectWithValue("Bet house not found");
      }
      return house;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load house detail"
      );
    }
  }
);

const upsertOperatorHouse = (state, house) => {
  if (!house?._id) return;
  const idx = state.houses.findIndex(
    (h) => String(h._id) === String(house._id)
  );
  if (idx === -1) {
    state.houses.push(house);
    state.houses.sort(
      (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
    );
  } else {
    state.houses[idx] = house;
  }
};

export const createBetHouse = createAsyncThunk(
  "operatorConsole/createBetHouse",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await api.post("/api/create-bet", payload);
      return res.data;
    } catch (error) {
      const data = error.response?.data;
      const message =
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        "Failed to create bet house";
      return rejectWithValue(message);
    }
  }
);

export const fetchOperatorDashboard = createAsyncThunk(
  "operatorConsole/fetchDashboard",
  async (houseId, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/dashboard`);
      return { houseId, data: res.data?.data || res.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load house dashboard");
    }
  }
);

export const fetchHouseAnalytics = createAsyncThunk(
  "operatorConsole/fetchHouseAnalytics",
  async ({ houseId, days = "30" }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/analytics`, {
        params: { days },
      });
      return { houseId, data: res.data?.data || null };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load house analytics"
      );
    }
  }
);

/**
 * Full treasury detail for the finance desk: summary pools + paginated ledger.
 * Backend: GET /api/bet/:houseId/treasury?page&limit&type&startDate&endDate
 * `type` must be a single ledger type (DEPOSIT, WITHDRAWAL, STAKE_RECEIVED,
 * LIABILITY_LOCKED, LIABILITY_RELEASED, PAYOUT_MADE, PLATFORM_COMMISSION).
 */
export const fetchHouseTreasury = createAsyncThunk(
  "operatorConsole/fetchHouseTreasury",
  async (
    { houseId, page = 1, limit = 25, type, startDate, endDate, append = false },
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit };
      if (type) params.type = type;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await api.get(`/api/bet/${houseId}/treasury`, { params });
      return { houseId, data: res.data?.data || null, append, type: type || null };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load treasury detail"
      );
    }
  }
);

export const fetchBetHouseCustomers = createAsyncThunk(
  "operatorConsole/fetchCustomers",
  async (
    {
      houseId,
      page = 1,
      limit = 20,
      search = "",
      sort = "recent",
      repeatOnly = false,
      minTickets = 0,
      append = false,
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search) params.set("search", search);
      if (sort) params.set("sort", sort);
      if (repeatOnly) params.set("repeatOnly", "true");
      if (minTickets > 0) params.set("minTickets", String(minTickets));

      const res = await api.get(`/api/bet/${houseId}/customers?${params.toString()}`);
      return {
        houseId,
        data: res.data?.data || [],
        pagination: res.data?.pagination || {},
        pulse: res.data?.pulse || null,
        append,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load bettors");
    }
  }
);

export const fetchBettorTickets = createAsyncThunk(
  "operatorConsole/fetchBettorTickets",
  async ({ houseId, bettorUserId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/bet/${houseId}/customers/${bettorUserId}/tickets`,
        { params: { userId: operatorUserId } }
      );
      const tickets = normalizeBettorTickets(
        Array.isArray(res.data?.data) ? res.data.data : []
      );

      let hydratedTickets = tickets;
      if (ticketsNeedFixtureHydration(tickets)) {
        hydratedTickets = await Promise.all(
          tickets.map(async (ticket) => {
            if (
              Number(ticket.legCount) <= 0 ||
              !ticketNeedsFixtureHydration(ticket)
            ) {
              return ticket;
            }
            try {
              return await hydrateOperatorTicket(
                houseId,
                ticket,
                operatorUserId,
                api
              );
            } catch (_error) {
              return ticket;
            }
          })
        );
      }

      hydratedTickets = await hydrateTicketLegScores(hydratedTickets, api);

      return {
        bettorUserId: String(bettorUserId),
        tickets: normalizeBettorTickets(hydratedTickets),
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load tickets");
    }
  }
);

export const fetchHouseTickets = createAsyncThunk(
  "operatorConsole/fetchHouseTickets",
  async (
    {
      houseId,
      operatorUserId,
      page = 1,
      limit = 20,
      search = "",
      status = "all",
      sort = "recent",
      append = false,
    },
    { rejectWithValue }
  ) => {
    try {
      const params = new URLSearchParams();
      params.set("userId", String(operatorUserId));
      params.set("page", String(page));
      params.set("limit", String(limit));
      if (search) params.set("search", search);
      if (status && status !== "all") params.set("status", status);
      if (sort && sort !== "recent") params.set("sort", sort);

      const res = await api.get(`/api/bet/${houseId}/tickets?${params.toString()}`);
      return {
        houseId,
        data: normalizeHouseTicketList(res.data?.data || []),
        pagination: res.data?.pagination || {},
        filteredPulse: res.data?.filteredPulse || null,
        housePulse: res.data?.housePulse || null,
        append,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load tickets");
    }
  }
);

export const fetchHouseTicketDetail = createAsyncThunk(
  "operatorConsole/fetchHouseTicketDetail",
  async ({ houseId, ticketId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/tickets/${ticketId}`, {
        params: { userId: operatorUserId },
      });
      const raw = res.data?.data || res.data;
      const ticket = await hydrateOperatorTicket(houseId, raw, operatorUserId, api);
      return { ticketId: String(ticketId), ticket };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load ticket");
    }
  }
);

export const fetchHouseViewers = createAsyncThunk(
  "operatorConsole/fetchHouseViewers",
  async (
    { houseId, operatorUserId, page = 1, limit = 40, append = false },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/viewers`, {
        params: { userId: operatorUserId, page, limit },
      });
      return {
        houseId,
        data: normalizeViewerList(res.data?.data || []),
        pulse: res.data?.pulse || null,
        pagination: res.data?.pagination || {},
        append,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load viewers");
    }
  }
);

export const fetchHouseFollowers = createAsyncThunk(
  "operatorConsole/fetchHouseFollowers",
  async (
    { houseId, operatorUserId, page = 1, limit = 40, append = false },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/followers`, {
        params: { userId: operatorUserId, page, limit },
      });
      return {
        houseId,
        data: normalizeFollowerList(res.data?.data || []),
        pulse: res.data?.pulse || null,
        pagination: res.data?.pagination || {},
        append,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to load followers");
    }
  }
);

export const fetchPartnerBalances = createAsyncThunk(
  "operatorConsole/fetchPartnerBalances",
  async ({ betId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bets/${betId}/partner-balances`, {
        params: { userId: operatorUserId },
      });
      return res.data?.data?.partners || [];
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load partner balances"
      );
    }
  }
);

const operatorApiErr = (error, fallback) => {
  const data = error.response?.data;
  if (data) {
    if (typeof data.message === "string" && data.message.trim()) return data.message;
    if (typeof data.error === "string" && data.error.trim()) return data.error;
  }
  if (error.code === "ERR_NETWORK" || error.message?.includes("Network Error")) {
    return "Could not reach api.betbond.app — check connection or wait for API deploy.";
  }
  return fallback;
};

const partnerApiErr = (error, fallback) => operatorApiErr(error, fallback);

export const fetchHousePartners = createAsyncThunk(
  "operatorConsole/fetchHousePartners",
  async ({ betId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet-house/${betId}/partners`, {
        params: { userId: operatorUserId },
      });
      return { betId, data: res.data?.data || null };
    } catch (error) {
      return rejectWithValue(
        partnerApiErr(error, "Failed to load partners")
      );
    }
  }
);

export const sendPartnerInvite = createAsyncThunk(
  "operatorConsole/sendPartnerInvite",
  async ({ betId, operatorUserId, inviteCode, expiresInHours = 24 }, { rejectWithValue }) => {
    try {
      await api.post(`/api/bet-house/${betId}/partners/invite`, {
        userId: operatorUserId,
        inviteCode,
        expiresInHours,
      });
      return { betId };
    } catch (error) {
      return rejectWithValue(partnerApiErr(error, "Invite failed"));
    }
  }
);

export const cancelPartnerInvite = createAsyncThunk(
  "operatorConsole/cancelPartnerInvite",
  async ({ betId, operatorUserId, requestId }, { rejectWithValue }) => {
    try {
      await api.post(`/api/bet-house/${betId}/partners/requests/${requestId}/cancel`, {
        userId: operatorUserId,
      });
      return { betId };
    } catch (error) {
      return rejectWithValue(partnerApiErr(error, "Cancel failed"));
    }
  }
);

export const resendPartnerInvite = createAsyncThunk(
  "operatorConsole/resendPartnerInvite",
  async (
    { betId, operatorUserId, requestId, expiresInHours = 24 },
    { rejectWithValue }
  ) => {
    try {
      await api.post(`/api/bet-house/${betId}/partners/requests/${requestId}/resend`, {
        userId: operatorUserId,
        expiresInHours,
      });
      return { betId };
    } catch (error) {
      return rejectWithValue(partnerApiErr(error, "Resend failed"));
    }
  }
);

export const removePartnerMember = createAsyncThunk(
  "operatorConsole/removePartnerMember",
  async ({ betId, operatorUserId, targetUserId }, { rejectWithValue }) => {
    try {
      await api.post(`/api/bet-house/${betId}/partners/remove`, {
        userId: operatorUserId,
        targetUserId,
      });
      return { betId };
    } catch (error) {
      return rejectWithValue(partnerApiErr(error, "Remove failed"));
    }
  }
);

export const leaveBetHouse = createAsyncThunk(
  "operatorConsole/leaveBetHouse",
  async ({ betId, operatorUserId }, { rejectWithValue }) => {
    try {
      await api.post(`/api/bet-house/${betId}/partners/leave`, {
        userId: operatorUserId,
      });
      return { betId };
    } catch (error) {
      return rejectWithValue(partnerApiErr(error, "Leave failed"));
    }
  }
);

export const fetchGoLiveReadiness = createAsyncThunk(
  "operatorConsole/fetchGoLiveReadiness",
  async ({ betId, userId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bets/${betId}/go-live-readiness`, {
        params: { userId },
      });
      return res.data?.data || res.data;
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "Could not load go-live checklist",
      });
    }
  }
);

export const updateBetHouseStatus = createAsyncThunk(
  "operatorConsole/updateBetHouseStatus",
  async ({ betId, userId, status, password }, { rejectWithValue }) => {
    try {
      const body = { userId, status };
      if (status === "closed" && password) {
        body.password = password;
      }
      const res = await api.patch(`/api/bets/${betId}/status`, body);
      return res.data?.bet || res.data;
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to change status",
        readiness: error.response?.data?.readiness,
      });
    }
  }
);

export const updateHouseBrand = createAsyncThunk(
  "operatorConsole/updateHouseBrand",
  async ({ betId, operatorUserId, name, brandStory }, { rejectWithValue }) => {
    try {
      const res = await api.patch(`/api/bets/${betId}/update-info`, {
        userId: operatorUserId,
        name,
        brandStory,
      });
      const patch = res.data?.data || {
        name: res.data?.bet?.name,
        brandStory: res.data?.bet?.brandStory,
        slug: res.data?.bet?.slug,
      };
      return { betId, patch, message: res.data?.message };
    } catch (error) {
      return rejectWithValue(operatorApiErr(error, "Failed to update house"));
    }
  }
);

export const updateHouseLimits = createAsyncThunk(
  "operatorConsole/updateHouseLimits",
  async (
    {
      betId,
      operatorUserId,
      minSelectionsPerBet,
      defaultMinBetAmount,
      defaultMaxBetAmount,
      matchingEnabled,
      matchingMaxPerTicket,
      matchingMaxTotalExposure,
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.patch(`/api/bets/${betId}/update-config`, {
        userId: operatorUserId,
        minSelectionsPerBet,
        defaultMinBetAmount,
        defaultMaxBetAmount,
        matchingEnabled,
        matchingMaxPerTicket,
        matchingMaxTotalExposure,
      });
      return {
        betId,
        patch: res.data?.data || {},
        message: res.data?.message,
      };
    } catch (error) {
      return rejectWithValue(operatorApiErr(error, "Failed to update limits"));
    }
  }
);

export const fetchPotentialMatchPartners = createAsyncThunk(
  "operatorConsole/fetchPotentialMatchPartners",
  async ({ houseId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/potential-partners`, {
        params: { userId: operatorUserId },
      });
      return {
        houseId,
        matchingEnabled: Boolean(res.data?.matchingEnabled),
        partners: res.data?.data || [],
      };
    } catch (error) {
      return rejectWithValue(
        operatorApiErr(error, "Failed to load potential partners")
      );
    }
  }
);

export const fetchMatchedTickets = createAsyncThunk(
  "operatorConsole/fetchMatchedTickets",
  async ({ houseId, operatorUserId, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/matched-tickets`, {
        params: { userId: operatorUserId, page, limit },
      });
      return {
        houseId,
        tickets: res.data?.data || [],
        pagination: res.data?.pagination || null,
      };
    } catch (error) {
      return rejectWithValue(
        operatorApiErr(error, "Failed to load matched tickets")
      );
    }
  }
);

export const fetchHouseFixtures = createAsyncThunk(
  "operatorConsole/fetchHouseFixtures",
  async ({ betId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bets/${betId}/added-fixtures`);
      return { betId, fixtures: res.data?.fixtures || [] };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || "Failed to load fixtures"
      );
    }
  }
);

export const addHouseFixture = createAsyncThunk(
  "operatorConsole/addHouseFixture",
  async ({ betId, fixtureId, operatorUserId }, { rejectWithValue }) => {
    try {
      await api.post(`/api/bets/${betId}/add-fixture/${fixtureId}`, {
        userId: operatorUserId,
      });
      return { betId, fixtureId };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.response?.data?.error || "Failed to add fixture"
      );
    }
  }
);

export const removeHouseFixture = createAsyncThunk(
  "operatorConsole/removeHouseFixture",
  async ({ betId, fixtureId, operatorUserId }, { rejectWithValue }) => {
    try {
      await api.post(`/api/bets/${betId}/remove-fixture/${fixtureId}`, {
        userId: operatorUserId,
      });
      return { betId, fixtureId: String(fixtureId) };
    } catch (error) {
      return rejectWithValue(operatorApiErr(error, "Failed to remove fixture"));
    }
  }
);

export const suspendHouseFixture = createAsyncThunk(
  "operatorConsole/suspendHouseFixture",
  async ({ betId, fixtureId, operatorUserId }, { rejectWithValue }) => {
    try {
      await api.patch(`/api/bet/${betId}/fixtures/${fixtureId}/suspend`, {
        userId: operatorUserId,
      });
      return { betId, fixtureId: String(fixtureId), suspended: true };
    } catch (error) {
      return rejectWithValue(operatorApiErr(error, "Failed to suspend fixture"));
    }
  }
);

export const fetchOddsHistory = createAsyncThunk(
  "operatorConsole/fetchOddsHistory",
  async ({ houseId, fixtureId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet/${houseId}/odds-history/${fixtureId}`);
      return {
        houseId,
        fixtureId,
        entries: Array.isArray(res.data?.entries) ? res.data.entries : [],
      };
    } catch (error) {
      return rejectWithValue(operatorApiErr(error, "Failed to load odds history"));
    }
  }
);

export const unsuspendHouseFixture = createAsyncThunk(
  "operatorConsole/unsuspendHouseFixture",
  async ({ betId, fixtureId, operatorUserId }, { rejectWithValue }) => {
    try {
      await api.patch(`/api/bet/${betId}/fixtures/${fixtureId}/unsuspend`, {
        userId: operatorUserId,
      });
      return { betId, fixtureId: String(fixtureId), suspended: false };
    } catch (error) {
      return rejectWithValue(operatorApiErr(error, "Failed to unsuspend fixture"));
    }
  }
);

export const fetchHouseLoanState = createAsyncThunk(
  "operatorConsole/fetchHouseLoanState",
  async ({ betId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet-house/${betId}/loan`);
      return { betId, data: res.data?.data || null };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load loan state"
      );
    }
  }
);

export const fetchHouseLoanEligibility = createAsyncThunk(
  "operatorConsole/fetchHouseLoanEligibility",
  async ({ betId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.get(`/api/bet-house/${betId}/loan/eligibility`, {
        params: { userId: operatorUserId },
      });
      return { betId, data: res.data?.data || null };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load loan eligibility"
      );
    }
  }
);

export const fetchHouseLoanDesk = createAsyncThunk(
  "operatorConsole/fetchHouseLoanDesk",
  async ({ betId, operatorUserId }, { rejectWithValue }) => {
    try {
      const [eligibilityRes, stateRes] = await Promise.all([
        api.get(`/api/bet-house/${betId}/loan/eligibility`, {
          params: { userId: operatorUserId },
        }),
        api.get(`/api/bet-house/${betId}/loan`),
      ]);
      return {
        betId,
        eligibility: eligibilityRes.data?.data || null,
        loanState: stateRes.data?.data || null,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to load loan desk"
      );
    }
  }
);

export const requestHouseLoan = createAsyncThunk(
  "operatorConsole/requestHouseLoan",
  async ({ betId, operatorUserId, loanPercent }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/bet-house/${betId}/loan/request`, {
        userId: operatorUserId,
        loanPercent,
      });
      return {
        betId,
        data: res.data?.data,
        betPatch: res.data?.data?.bet,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to request loan"
      );
    }
  }
);

export const payHouseLoan = createAsyncThunk(
  "operatorConsole/payHouseLoan",
  async ({ betId, operatorUserId }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/bet-house/${betId}/loan/pay`, {
        userId: operatorUserId,
      });
      return {
        betId,
        data: res.data?.data,
        betPatch: res.data?.data?.bet,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to pay loan"
      );
    }
  }
);

export const depositHouseBalance = createAsyncThunk(
  "operatorConsole/depositHouseBalance",
  async ({ betId, operatorUserId, amount }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/bets/${betId}/add-balance`, {
        userId: operatorUserId,
        amount,
      });
      return {
        betId,
        bet: res.data?.data?.bet,
        userCash: res.data?.data?.userCash,
        message: res.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to deposit"
      );
    }
  }
);

export const withdrawHouseBalance = createAsyncThunk(
  "operatorConsole/withdrawHouseBalance",
  async ({ betId, operatorUserId, amount }, { rejectWithValue }) => {
    try {
      const res = await api.post(`/api/bets/${betId}/withdraw-balance`, {
        userId: operatorUserId,
        amount,
      });
      return {
        betId,
        bet: res.data?.data?.bet,
        userCash: res.data?.data?.userCash,
        message: res.data?.message,
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to withdraw"
      );
    }
  }
);

export const rewardBettor = createAsyncThunk(
  "operatorConsole/rewardBettor",
  async (
    { houseId, bettorUserId, operatorUserId, currency, amount, reason },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/api/bet/${houseId}/customers/${bettorUserId}/reward`,
        {
          userId: operatorUserId,
          currency,
          amount,
          reason,
        }
      );
      return res.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to send reward"
      );
    }
  }
);

const operatorConsoleSlice = createSlice({
  name: "operatorConsole",
  initialState: {
    houses: [],
    housesStatus: "idle",
    housesError: null,
    houseDetailStatusById: {},
    dashboard: null,
    dashboardHouseId: null,
    dashboardStatus: "idle",
    dashboardError: null,
    dashboardsByHouse: {},
    dashboardStatusByHouse: {},
    dashboardErrorByHouse: {},
    analytics: null,
    analyticsHouseId: null,
    analyticsStatus: "idle",
    analyticsError: null,
    treasuryDetail: null,
    treasuryDetailHouseId: null,
    treasuryDetailStatus: "idle",
    treasuryDetailError: null,
    treasuryLedger: [],
    treasuryLedgerPagination: null,
    treasuryLedgerType: null,
    treasuryFlowEntries: [],
    loadingMoreTreasuryLedger: false,
    customers: [],
    customersHouseId: null,
    customersPagination: null,
    customersPulse: null,
    customersStatus: "idle",
    customersError: null,
    loadingMoreCustomers: false,
    houseTickets: [],
    houseTicketsHouseId: null,
    houseTicketsPagination: null,
    houseTicketsStatus: "idle",
    houseTicketsError: null,
    loadingMoreHouseTickets: false,
    houseTicketsFilteredPulse: null,
    houseTicketsHousePulse: null,
    houseViewers: [],
    houseViewersHouseId: null,
    houseViewersPagination: null,
    houseViewersPulse: null,
    houseViewersStatus: "idle",
    houseViewersError: null,
    loadingMoreHouseViewers: false,
    houseFollowers: [],
    houseFollowersHouseId: null,
    houseFollowersPagination: null,
    houseFollowersPulse: null,
    houseFollowersStatus: "idle",
    houseFollowersError: null,
    loadingMoreHouseFollowers: false,
    houseLoanEligibility: null,
    houseLoanState: null,
    houseLoanHouseId: null,
    houseLoanStatus: "idle",
    houseLoanError: null,
    houseLoanRequesting: false,
    houseLoanPaying: false,
    housePartnersData: null,
    housePartnersHouseId: null,
    housePartnersStatus: "idle",
    housePartnersError: null,
    housePartnersActionLoading: false,
    houseFixtures: [],
    houseFixturesHouseId: null,
    houseFixturesStatus: "idle",
    houseFixturesError: null,
    houseFixturesActionLoading: false,
    oddsHistoryEntries: [],
    oddsHistoryKey: null,
    oddsHistoryStatus: "idle",
    oddsHistoryError: null,
    houseSettingsSaving: false,
    houseSettingsError: null,
    potentialPartners: [],
    potentialPartnersHouseId: null,
    potentialPartnersStatus: "idle",
    potentialPartnersError: null,
    matchedTickets: [],
    matchedTicketsHouseId: null,
    matchedTicketsPagination: null,
    matchedTicketsStatus: "idle",
    matchedTicketsError: null,
  },
  reducers: {
    clearOperatorDashboard: (state) => {
      state.dashboard = null;
      state.dashboardHouseId = null;
      state.dashboardStatus = "idle";
      state.dashboardError = null;
    },
    clearHouseTreasuryDetail: (state) => {
      state.treasuryDetail = null;
      state.treasuryDetailHouseId = null;
      state.treasuryDetailStatus = "idle";
      state.treasuryDetailError = null;
      state.treasuryLedger = [];
      state.treasuryLedgerPagination = null;
      state.treasuryLedgerType = null;
      state.treasuryFlowEntries = [];
      state.loadingMoreTreasuryLedger = false;
    },
    clearBetHouseCustomers: (state) => {
      state.customers = [];
      state.customersHouseId = null;
      state.customersPagination = null;
      state.customersPulse = null;
      state.customersStatus = "idle";
      state.customersError = null;
      state.loadingMoreCustomers = false;
    },
    clearHouseTickets: (state) => {
      state.houseTickets = [];
      state.houseTicketsHouseId = null;
      state.houseTicketsPagination = null;
      state.houseTicketsStatus = "idle";
      state.houseTicketsError = null;
      state.loadingMoreHouseTickets = false;
      state.houseTicketsFilteredPulse = null;
      state.houseTicketsHousePulse = null;
    },
    clearHouseViewers: (state) => {
      state.houseViewers = [];
      state.houseViewersHouseId = null;
      state.houseViewersPagination = null;
      state.houseViewersPulse = null;
      state.houseViewersStatus = "idle";
      state.houseViewersError = null;
      state.loadingMoreHouseViewers = false;
    },
    clearHouseFollowers: (state) => {
      state.houseFollowers = [];
      state.houseFollowersHouseId = null;
      state.houseFollowersPagination = null;
      state.houseFollowersPulse = null;
      state.houseFollowersStatus = "idle";
      state.houseFollowersError = null;
      state.loadingMoreHouseFollowers = false;
    },
    clearHouseLoan: (state) => {
      state.houseLoanEligibility = null;
      state.houseLoanState = null;
      state.houseLoanHouseId = null;
      state.houseLoanStatus = "idle";
      state.houseLoanError = null;
      state.houseLoanRequesting = false;
      state.houseLoanPaying = false;
    },
    clearHousePartners: (state) => {
      state.housePartnersData = null;
      state.housePartnersHouseId = null;
      state.housePartnersStatus = "idle";
      state.housePartnersError = null;
      state.housePartnersActionLoading = false;
    },
    clearHouseFixtures: (state) => {
      state.houseFixtures = [];
      state.houseFixturesHouseId = null;
      state.houseFixturesStatus = "idle";
      state.houseFixturesError = null;
      state.houseFixturesActionLoading = false;
    },
    clearOddsHistory: (state) => {
      state.oddsHistoryEntries = [];
      state.oddsHistoryKey = null;
      state.oddsHistoryStatus = "idle";
      state.oddsHistoryError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOperatorHouses.pending, (state) => {
        state.housesStatus = "loading";
        state.housesError = null;
      })
      .addCase(fetchOperatorHouses.fulfilled, (state, action) => {
        state.housesStatus = "succeeded";
        state.houses = action.payload;
        action.payload.forEach((house) => {
          state.houseDetailStatusById[String(house._id)] = "succeeded";
        });
      })
      .addCase(fetchOperatorHouses.rejected, (state, action) => {
        state.housesStatus = "failed";
        state.housesError = action.payload;
      })
      .addCase(fetchOperatorHousesSummary.pending, (state) => {
        state.housesStatus = "loading";
        state.housesError = null;
      })
      .addCase(fetchOperatorHousesSummary.fulfilled, (state, action) => {
        state.housesStatus = "succeeded";
        state.houses = action.payload;
        action.payload.forEach((house) => {
          const id = String(house._id);
          if (!state.houseDetailStatusById[id]) {
            state.houseDetailStatusById[id] = "idle";
          }
        });
      })
      .addCase(fetchOperatorHousesSummary.rejected, (state, action) => {
        state.housesStatus = "failed";
        state.housesError = action.payload;
      })
      .addCase(fetchOperatorHouseDetail.pending, (state, action) => {
        const id = String(action.meta.arg.betId);
        state.houseDetailStatusById[id] = "loading";
      })
      .addCase(fetchOperatorHouseDetail.fulfilled, (state, action) => {
        const id = String(action.payload._id);
        upsertOperatorHouse(state, action.payload);
        state.houseDetailStatusById[id] = "succeeded";
      })
      .addCase(fetchOperatorHouseDetail.rejected, (state, action) => {
        const id = String(action.meta.arg.betId);
        state.houseDetailStatusById[id] = "failed";
      })
      .addCase(fetchOperatorDashboard.pending, (state, action) => {
        const houseId = String(action.meta.arg);
        state.dashboardStatus = "loading";
        state.dashboardError = null;
        state.dashboardStatusByHouse[houseId] = "loading";
        state.dashboardErrorByHouse[houseId] = null;
      })
      .addCase(fetchOperatorDashboard.fulfilled, (state, action) => {
        const houseId = String(action.payload.houseId);
        state.dashboardStatus = "succeeded";
        state.dashboardHouseId = action.payload.houseId;
        state.dashboard = action.payload.data;
        state.dashboardsByHouse[houseId] = action.payload.data;
        state.dashboardStatusByHouse[houseId] = "succeeded";
        state.dashboardErrorByHouse[houseId] = null;
      })
      .addCase(fetchOperatorDashboard.rejected, (state, action) => {
        const houseId = String(action.meta.arg);
        state.dashboardStatus = "failed";
        state.dashboardError = action.payload;
        state.dashboardStatusByHouse[houseId] = "failed";
        state.dashboardErrorByHouse[houseId] = action.payload;
      })
      .addCase(fetchHouseAnalytics.pending, (state) => {
        state.analyticsStatus = "loading";
        state.analyticsError = null;
      })
      .addCase(fetchHouseAnalytics.fulfilled, (state, action) => {
        state.analyticsStatus = "succeeded";
        state.analyticsHouseId = action.payload.houseId;
        state.analytics = action.payload.data;
      })
      .addCase(fetchHouseAnalytics.rejected, (state, action) => {
        state.analyticsStatus = "failed";
        state.analyticsError = action.payload;
      })
      .addCase(fetchHouseTreasury.pending, (state, action) => {
        if (action.meta.arg?.append) {
          state.loadingMoreTreasuryLedger = true;
        } else {
          state.treasuryDetailStatus = "loading";
        }
        state.treasuryDetailError = null;
      })
      .addCase(fetchHouseTreasury.fulfilled, (state, action) => {
        state.treasuryDetailStatus = "succeeded";
        state.loadingMoreTreasuryLedger = false;
        state.treasuryDetailHouseId = action.payload.houseId;
        const data = action.payload.data || {};
        const { ledgerEntries, ledgerPagination, ...summary } = data;
        state.treasuryDetail = summary;
        const rows = Array.isArray(ledgerEntries) ? ledgerEntries : [];
        if (action.payload.append) {
          state.treasuryLedger = [...state.treasuryLedger, ...rows];
        } else {
          state.treasuryLedger = rows;
        }
        state.treasuryLedgerPagination = ledgerPagination || null;
        state.treasuryLedgerType = action.payload.type;
        // Unfiltered fetches also feed the cashflow chart dataset.
        if (!action.payload.type) {
          state.treasuryFlowEntries = action.payload.append
            ? [...state.treasuryFlowEntries, ...rows]
            : rows;
        }
      })
      .addCase(fetchHouseTreasury.rejected, (state, action) => {
        state.treasuryDetailStatus = "failed";
        state.loadingMoreTreasuryLedger = false;
        state.treasuryDetailError = action.payload;
      })
      .addCase(fetchBetHouseCustomers.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMoreCustomers = true;
        } else {
          state.customersStatus = "loading";
        }
        state.customersError = null;
      })
      .addCase(fetchBetHouseCustomers.fulfilled, (state, action) => {
        state.customersStatus = "succeeded";
        state.loadingMoreCustomers = false;
        state.customersHouseId = action.payload.houseId;
        const rows = action.payload.data || [];
        if (action.payload.append) {
          state.customers = [...state.customers, ...rows];
        } else {
          state.customers = rows;
        }
        state.customersPagination = action.payload.pagination;
        if (!action.payload.append) {
          state.customersPulse = action.payload.pulse;
        }
      })
      .addCase(fetchBetHouseCustomers.rejected, (state, action) => {
        state.customersStatus = "failed";
        state.loadingMoreCustomers = false;
        state.customersError = action.payload;
      })
      .addCase(fetchHouseTickets.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMoreHouseTickets = true;
        } else {
          state.houseTicketsStatus = "loading";
        }
        state.houseTicketsError = null;
      })
      .addCase(fetchHouseTickets.fulfilled, (state, action) => {
        state.houseTicketsStatus = "succeeded";
        state.loadingMoreHouseTickets = false;
        state.houseTicketsHouseId = action.payload.houseId;
        const rows = action.payload.data || [];
        if (action.payload.append) {
          state.houseTickets = [...state.houseTickets, ...rows];
        } else {
          state.houseTickets = rows;
        }
        state.houseTicketsPagination = action.payload.pagination;
        if (!action.payload.append) {
          state.houseTicketsFilteredPulse = action.payload.filteredPulse;
          state.houseTicketsHousePulse = action.payload.housePulse;
        }
      })
      .addCase(fetchHouseTickets.rejected, (state, action) => {
        state.houseTicketsStatus = "failed";
        state.loadingMoreHouseTickets = false;
        state.houseTicketsError = action.payload;
      })
      .addCase(fetchHouseViewers.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMoreHouseViewers = true;
        } else {
          state.houseViewersStatus = "loading";
        }
        state.houseViewersError = null;
      })
      .addCase(fetchHouseViewers.fulfilled, (state, action) => {
        state.houseViewersStatus = "succeeded";
        state.loadingMoreHouseViewers = false;
        state.houseViewersHouseId = action.payload.houseId;
        const rows = action.payload.data || [];
        if (action.payload.append) {
          state.houseViewers = [...state.houseViewers, ...rows];
        } else {
          state.houseViewers = rows;
        }
        state.houseViewersPagination = action.payload.pagination;
        if (!action.payload.append) {
          state.houseViewersPulse = action.payload.pulse;
        }
      })
      .addCase(fetchHouseViewers.rejected, (state, action) => {
        state.houseViewersStatus = "failed";
        state.loadingMoreHouseViewers = false;
        state.houseViewersError = action.payload;
      })
      .addCase(fetchHouseFollowers.pending, (state, action) => {
        const append = action.meta.arg?.append;
        if (append) {
          state.loadingMoreHouseFollowers = true;
        } else {
          state.houseFollowersStatus = "loading";
        }
        state.houseFollowersError = null;
      })
      .addCase(fetchHouseFollowers.fulfilled, (state, action) => {
        state.houseFollowersStatus = "succeeded";
        state.loadingMoreHouseFollowers = false;
        state.houseFollowersHouseId = action.payload.houseId;
        const rows = action.payload.data || [];
        if (action.payload.append) {
          state.houseFollowers = [...state.houseFollowers, ...rows];
        } else {
          state.houseFollowers = rows;
        }
        state.houseFollowersPagination = action.payload.pagination;
        if (!action.payload.append) {
          state.houseFollowersPulse = action.payload.pulse;
        }
      })
      .addCase(fetchHouseFollowers.rejected, (state, action) => {
        state.houseFollowersStatus = "failed";
        state.loadingMoreHouseFollowers = false;
        state.houseFollowersError = action.payload;
      })
      .addCase(depositHouseBalance.fulfilled, (state, action) => {
        const updated = action.payload.bet;
        if (!updated?._id) return;
        const idx = state.houses.findIndex(
          (h) => String(h._id) === String(updated._id)
        );
        if (idx !== -1) state.houses[idx] = updated;
      })
      .addCase(withdrawHouseBalance.fulfilled, (state, action) => {
        const updated = action.payload.bet;
        if (!updated?._id) return;
        const idx = state.houses.findIndex(
          (h) => String(h._id) === String(updated._id)
        );
        if (idx !== -1) state.houses[idx] = updated;
      })
      .addCase(fetchHouseLoanDesk.pending, (state) => {
        state.houseLoanStatus = "loading";
        state.houseLoanError = null;
      })
      .addCase(fetchHouseLoanDesk.fulfilled, (state, action) => {
        state.houseLoanStatus = "succeeded";
        state.houseLoanHouseId = action.payload.betId;
        state.houseLoanEligibility = action.payload.eligibility;
        state.houseLoanState = action.payload.loanState;
      })
      .addCase(fetchHouseLoanDesk.rejected, (state, action) => {
        state.houseLoanStatus = "failed";
        state.houseLoanError = action.payload;
      })
      .addCase(requestHouseLoan.pending, (state) => {
        state.houseLoanRequesting = true;
        state.houseLoanError = null;
      })
      .addCase(requestHouseLoan.fulfilled, (state, action) => {
        state.houseLoanRequesting = false;
        const patch = action.payload.betPatch;
        if (patch?._id) {
          const idx = state.houses.findIndex(
            (h) => String(h._id) === String(patch._id)
          );
          if (idx !== -1) {
            state.houses[idx] = { ...state.houses[idx], ...patch };
          }
        }
      })
      .addCase(requestHouseLoan.rejected, (state, action) => {
        state.houseLoanRequesting = false;
        state.houseLoanError = action.payload;
      })
      .addCase(payHouseLoan.pending, (state) => {
        state.houseLoanPaying = true;
        state.houseLoanError = null;
      })
      .addCase(payHouseLoan.fulfilled, (state, action) => {
        state.houseLoanPaying = false;
        const patch = action.payload.betPatch;
        if (patch?._id) {
          const idx = state.houses.findIndex(
            (h) => String(h._id) === String(patch._id)
          );
          if (idx !== -1) {
            state.houses[idx] = { ...state.houses[idx], ...patch };
          }
        }
      })
      .addCase(payHouseLoan.rejected, (state, action) => {
        state.houseLoanPaying = false;
        state.houseLoanError = action.payload;
      })
      .addCase(fetchHousePartners.pending, (state) => {
        state.housePartnersStatus = "loading";
        state.housePartnersError = null;
      })
      .addCase(fetchHousePartners.fulfilled, (state, action) => {
        state.housePartnersStatus = "succeeded";
        state.housePartnersHouseId = action.payload.betId;
        state.housePartnersData = action.payload.data;
      })
      .addCase(fetchHousePartners.rejected, (state, action) => {
        state.housePartnersStatus = "failed";
        state.housePartnersError = action.payload;
      })
      .addCase(fetchHouseFixtures.pending, (state) => {
        state.houseFixturesStatus = "loading";
        state.houseFixturesError = null;
      })
      .addCase(fetchHouseFixtures.fulfilled, (state, action) => {
        state.houseFixturesStatus = "succeeded";
        state.houseFixturesHouseId = action.payload.betId;
        state.houseFixtures = action.payload.fixtures || [];
      })
      .addCase(fetchHouseFixtures.rejected, (state, action) => {
        state.houseFixturesStatus = "failed";
        state.houseFixturesError = action.payload;
      })
      .addCase(fetchOddsHistory.pending, (state) => {
        state.oddsHistoryStatus = "loading";
        state.oddsHistoryError = null;
      })
      .addCase(fetchOddsHistory.fulfilled, (state, action) => {
        state.oddsHistoryStatus = "succeeded";
        const { houseId, fixtureId, entries } = action.payload;
        state.oddsHistoryKey = `${houseId}:${fixtureId}`;
        state.oddsHistoryEntries = entries;
      })
      .addCase(fetchOddsHistory.rejected, (state, action) => {
        state.oddsHistoryStatus = "failed";
        state.oddsHistoryError = action.payload;
      })
      .addCase(updateHouseBrand.pending, (state) => {
        state.houseSettingsSaving = true;
        state.houseSettingsError = null;
      })
      .addCase(updateHouseBrand.fulfilled, (state, action) => {
        state.houseSettingsSaving = false;
        const { betId, patch } = action.payload;
        const idx = state.houses.findIndex((h) => String(h._id) === String(betId));
        if (idx !== -1 && patch) {
          state.houses[idx] = { ...state.houses[idx], ...patch };
        }
      })
      .addCase(updateHouseBrand.rejected, (state, action) => {
        state.houseSettingsSaving = false;
        state.houseSettingsError = action.payload;
      })
      .addCase(updateHouseLimits.pending, (state) => {
        state.houseSettingsSaving = true;
        state.houseSettingsError = null;
      })
      .addCase(updateHouseLimits.fulfilled, (state, action) => {
        state.houseSettingsSaving = false;
        const { betId, patch } = action.payload;
        const idx = state.houses.findIndex((h) => String(h._id) === String(betId));
        if (idx !== -1 && patch) {
          state.houses[idx] = { ...state.houses[idx], ...patch };
        }
      })
      .addCase(updateHouseLimits.rejected, (state, action) => {
        state.houseSettingsSaving = false;
        state.houseSettingsError = action.payload;
      })
      .addCase(fetchPotentialMatchPartners.pending, (state) => {
        state.potentialPartnersStatus = "loading";
        state.potentialPartnersError = null;
      })
      .addCase(fetchPotentialMatchPartners.fulfilled, (state, action) => {
        state.potentialPartnersStatus = "succeeded";
        state.potentialPartners = action.payload.partners;
        state.potentialPartnersHouseId = action.payload.houseId;
      })
      .addCase(fetchPotentialMatchPartners.rejected, (state, action) => {
        state.potentialPartnersStatus = "failed";
        state.potentialPartnersError = action.payload;
      })
      .addCase(fetchMatchedTickets.pending, (state) => {
        state.matchedTicketsStatus = "loading";
        state.matchedTicketsError = null;
      })
      .addCase(fetchMatchedTickets.fulfilled, (state, action) => {
        state.matchedTicketsStatus = "succeeded";
        state.matchedTickets = action.payload.tickets;
        state.matchedTicketsHouseId = action.payload.houseId;
        state.matchedTicketsPagination = action.payload.pagination;
      })
      .addCase(fetchMatchedTickets.rejected, (state, action) => {
        state.matchedTicketsStatus = "failed";
        state.matchedTicketsError = action.payload;
      });

    const partnerActionThunks = [
      sendPartnerInvite,
      cancelPartnerInvite,
      resendPartnerInvite,
      removePartnerMember,
      leaveBetHouse,
    ];
    const fixtureActionThunks = [
      addHouseFixture,
      removeHouseFixture,
      suspendHouseFixture,
      unsuspendHouseFixture,
    ];

    partnerActionThunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.housePartnersActionLoading = true;
          state.housePartnersError = null;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.housePartnersActionLoading = false;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.housePartnersActionLoading = false;
          state.housePartnersError = action.payload;
        });
    });

    fixtureActionThunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.houseFixturesActionLoading = true;
          state.houseFixturesError = null;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.houseFixturesActionLoading = false;
          state.houseFixturesError = action.payload;
        });
    });

    builder
      .addCase(addHouseFixture.fulfilled, (state) => {
        state.houseFixturesActionLoading = false;
      })
      .addCase(suspendHouseFixture.fulfilled, (state, action) => {
        state.houseFixturesActionLoading = false;
        const id = action.payload.fixtureId;
        const row = state.houseFixtures.find(
          (f) => String(f._id || f.id) === id
        );
        if (row) {
          row.suspended = true;
          row.suspendedManually = true;
        }
      })
      .addCase(unsuspendHouseFixture.fulfilled, (state, action) => {
        state.houseFixturesActionLoading = false;
        const id = action.payload.fixtureId;
        const row = state.houseFixtures.find(
          (f) => String(f._id || f.id) === id
        );
        if (row) {
          row.suspended = false;
          row.suspendedManually = false;
          row.suspendedAt = null;
        }
      })
      .addCase(removeHouseFixture.fulfilled, (state, action) => {
        state.houseFixturesActionLoading = false;
        const id = action.payload.fixtureId;
        state.houseFixtures = state.houseFixtures.filter(
          (f) => String(f._id || f.id) !== id
        );
      })
      .addCase(updateBetHouseStatus.fulfilled, (state, action) => {
        const updatedBet = action.payload;
        if (!updatedBet?._id) return;
        const idx = state.houses.findIndex(
          (h) => String(h._id) === String(updatedBet._id)
        );
        if (idx !== -1) {
          state.houses[idx] = { ...state.houses[idx], ...updatedBet };
        }
      });
  },
});

export const {
  clearOperatorDashboard,
  clearHouseTreasuryDetail,
  clearBetHouseCustomers,
  clearHouseTickets,
  clearHouseViewers,
  clearHouseFollowers,
  clearHouseLoan,
  clearHousePartners,
  clearHouseFixtures,
  clearOddsHistory,
} = operatorConsoleSlice.actions;
export default operatorConsoleSlice.reducer;
